/**
 * Wayfinding panel — pick a start and a destination, get a routed path.
 *
 * The route itself is computed in data/wayfinding.js; this is the picker plus
 * the step list. Selecting a step highlights nothing in 3D yet — the animated
 * ribbon on the map already carries the direction of travel.
 */

import React, { useMemo, useState } from 'react';
import {
    Navigation, X, ArrowRight, ArrowLeft, ArrowUp, ArrowDown,
    MapPin, Flag, Footprints, Clock, ArrowUpDown, Repeat,
} from 'lucide-react';
import SAMPLE_ROUTER from '../data/wayfinding';

function EndpointPicker({ label, value, onChange, placeholder, icon, endpoints }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);

    const selected = value ? endpoints.find((e) => e.id === value) : null;

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return endpoints.slice(0, 40);
        return endpoints
            .filter((e) => e.label.toLowerCase().includes(q) || e.sub.toLowerCase().includes(q))
            .slice(0, 40);
    }, [query, endpoints]);

    return (
        <div className="mm-picker">
            <label>{icon}{label}</label>
            {selected && !open ? (
                <button type="button" className="mm-picker-selected" onClick={() => { setOpen(true); setQuery(''); }}>
                    <span>{selected.label}</span>
                    <em>{selected.sub}</em>
                </button>
            ) : (
                <input
                    className="mm-picker-input"
                    value={query}
                    placeholder={placeholder}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    autoComplete="off"
                />
            )}
            {open && (
                <ul className="mm-picker-list">
                    {matches.map((m) => (
                        <li key={m.id}>
                            <button
                                type="button"
                                onClick={() => { onChange(m.id); setOpen(false); setQuery(''); }}
                            >
                                <span>{m.label}</span>
                                <em>{m.sub}</em>
                            </button>
                        </li>
                    ))}
                    {matches.length === 0 && <li className="mm-picker-empty">No match</li>}
                </ul>
            )}
        </div>
    );
}

const STEP_ICON = {
    start: <MapPin size={14} />,
    arrive: <Flag size={14} />,
    walk: <Footprints size={14} />,
    vertical: <ArrowUpDown size={14} />,
};

function stepIcon(step) {
    if (step.kind === 'turn') {
        return step.text.includes('right') ? <ArrowRight size={14} /> : <ArrowLeft size={14} />;
    }
    if (step.kind === 'vertical') {
        return step.text.includes(' up ') ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
    }
    return STEP_ICON[step.kind] || <Footprints size={14} />;
}

/**
 * `router` is injected so the panel serves the sample estate and an imported
 * plan identically — the imported one builds its graph from traced circulation
 * rather than declared corridors, but the interface is the same.
 */
export default function DirectionsPanel({ fromId, toId, onChange, onClose, onRoute, router = SAMPLE_ROUTER }) {
    const endpoints = router ? router.endpoints : [];

    const route = useMemo(() => {
        if (!router || !fromId || !toId || fromId === toId) return null;
        return router.findRoute(fromId, toId);
    }, [router, fromId, toId]);

    // Hand the computed route up so the 3D scene can draw it.
    React.useEffect(() => { onRoute(route); }, [route, onRoute]);

    const swap = () => onChange({ fromId: toId, toId: fromId });

    return (
        <div className="mm-directions">
            <header className="mm-directions-head">
                <h4><Navigation size={15} /> Wayfinding</h4>
                <button type="button" onClick={onClose} aria-label="Close directions"><X size={16} /></button>
            </header>

            <div className="mm-directions-form">
                <EndpointPicker
                    label="From"
                    icon={<MapPin size={13} />}
                    value={fromId}
                    endpoints={endpoints}
                    placeholder="Search a store or amenity…"
                    onChange={(id) => onChange({ fromId: id, toId })}
                />
                <button type="button" className="mm-swap" onClick={swap} aria-label="Swap start and destination">
                    <Repeat size={14} />
                </button>
                <EndpointPicker
                    label="To"
                    icon={<Flag size={13} />}
                    value={toId}
                    endpoints={endpoints}
                    placeholder="Search a store or amenity…"
                    onChange={(id) => onChange({ fromId, toId: id })}
                />
            </div>

            {route && (
                <>
                    <div className="mm-route-summary">
                        <div><Footprints size={15} /><strong>{route.distance} m</strong><span>walking</span></div>
                        <div><Clock size={15} /><strong>{route.minutes} min</strong><span>at a mall pace</span></div>
                        {route.floorChanges > 0 && (
                            <div><ArrowUpDown size={15} /><strong>{route.floorChanges}</strong><span>level change{route.floorChanges > 1 ? 's' : ''}</span></div>
                        )}
                    </div>

                    <ol className="mm-steps">
                        {route.steps.map((s, i) => (
                            <li key={`${s.kind}-${i}`} className={`mm-step is-${s.kind}`}>
                                <span className="mm-step-icon">{stepIcon(s)}</span>
                                <span className="mm-step-text">
                                    <strong>{s.text}</strong>
                                    {s.detail && <em>{s.detail}</em>}
                                </span>
                            </li>
                        ))}
                    </ol>
                </>
            )}

            {!route && fromId && toId && fromId !== toId && (
                <p className="mm-note">No walkable route between those two points.</p>
            )}
            {(!fromId || !toId) && (
                <p className="mm-note">Choose a start and a destination to plot a route across the floor plates.</p>
            )}
            {!endpoints.length && (
                <p className="mm-note">
                    No walkable space was traced on this plan, so there is nothing to route along.
                    Re-import it and check that the malls and atria came back as circulation.
                </p>
            )}
        </div>
    );
}
